import React from "react";
import {useStores} from "~/stores";
import {C} from "~/configurations/active-configuration";
import type {AdvancedFilter, BuGroupField} from "~/services/ApiTypes";
import type {SelectionResult} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {MultiSelection} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {DEFAULT} from "~/components/visualization/api-dropdown-filter/ApiDropdownFilterComponent";
import {
    CATEGORY_LEVEL_KEYS,
    countryListFromSelection,
    countrySelection
} from "~/stores/managers/spend-landscape/spendLandscapeFilters";
import {InsightOptionFilter} from "./InsightOptionFilter";
import {DateRangeFilter} from "./DateRangeFilter";
import {makeOptionSources} from "./optionSources";
import {continentOptions, continentsOfCountries, countriesOfContinents, countryOptions} from "./geoOptions";

const PRIMARY_ORDER = ['date_range', 'category_l1', 'category_l2', 'business_unit_ids', 's_ids'];

export type FilterDefinition = {
    id: string
    label: string
    /** Always shown in the bar; the others are added with "Add filter" */
    primary: boolean
    isActive: (filter: AdvancedFilter) => boolean
    render: () => React.ReactNode
}

const listSelection = (values: string[] | undefined): SelectionResult => ({inclusive: true, selected: (values ?? []).map(id => ({id}))});
const listFromSelection = (s: SelectionResult): string[] | undefined => {
    const ids = s.selected.map(o => String(o.id));
    return ids.length > 0 ? ids : undefined;
};

/**
 * The filters of the Spend Landscape filter bar for this customer and databag.
 */
export function useFilterDefinitions(): FilterDefinition[] {
    const {p, bagStore, materializedApi} = useStores();
    const manager = bagStore.advanced;
    const editing = manager.filterStore.editingFilters;
    const sources = React.useMemo(() => makeOptionSources(materializedApi), [materializedApi]);
    const geoData = manager.spendCountryData;
    const countrySource = React.useMemo(() =>
        (_: number, __: unknown, search: string) => Promise.resolve(countryOptions(geoData, search)), [geoData]);
    const continentSource = React.useMemo(() =>
        (_: number, __: unknown, search: string) => Promise.resolve(continentOptions(geoData, search)), [geoData]);

    const showL4 = Boolean(p.p.showL4InOpportunityDashboard);
    const hideParents = Boolean(p.p.hideParentSupplierOpportunityDashboard);
    const parentLabel = p.p.parentSupplierAltName ?? 'Parent supplier';
    const geoRegions = manager.hasGeoRegionFilter;

    const definitions: (FilterDefinition | false)[] = [
        ...([1, 2, 3, 4] as const).filter(level => level < 4 || showL4).map(level => {
            const key = CATEGORY_LEVEL_KEYS[level - 1];
            const label = `Category L${level}`;
            return {
                id: key, label, primary: level <= 2,
                isActive: (f: AdvancedFilter) => MultiSelection.isActive(f[key]),
                render: () => <InsightOptionFilter
                    label={label}
                    selection={editing[key] ?? DEFAULT}
                    exclude={[key, `${key}__not`]}
                    source={level === 1 ? sources.categoryL1 : sources.categoryLevel(level)}
                    showOptionSubLabel={level > 1}
                    onChange={s => ({[key]: s as SelectionResult<string>})}/>,
            };
        }),
        !geoRegions && {
            id: 'country', label: 'Country', primary: false,
            isActive: f => (f.country?.length ?? 0) > 0,
            render: () => <InsightOptionFilter
                label="Country"
                selection={countrySelection(editing.country)}
                exclude={['country']}
                source={countrySource}
                onChange={s => ({country: countryListFromSelection(s, geoData.map(d => d.country))})}/>,
        },
        !geoRegions && {
            id: 'continent', label: 'Continent', primary: false,
            isActive: () => false,
            render: () => <InsightOptionFilter
                label="Continent"
                selection={listSelection(continentsOfCountries(geoData, editing.country))}
                exclude={['country']}
                source={continentSource}
                inclusiveOnly
                onChange={s => {
                    const countries = countriesOfContinents(geoData, listFromSelection(s) ?? []);
                    return {country: countries.length > 0 ? countries : undefined};
                }}/>,
        },
        geoRegions && {
            id: 'geo_regions', label: p.regionDataLabel, primary: false,
            isActive: f => MultiSelection.isActive(f.geo_regions),
            render: () => <InsightOptionFilter
                label={p.regionDataLabel}
                selection={editing.geo_regions ?? DEFAULT}
                exclude={['geo_regions', 'geo_regions__not']}
                source={sources.geoRegion}
                showOptionSubLabel
                onChange={s => ({geo_regions: s as SelectionResult<number | null>})}/>,
        },
        !C.profile.disableAdvDashboardSearchBySupplier && {
            id: 's_ids', label: p.supplierDataName, primary: true,
            isActive: f => MultiSelection.isActive(f.s_ids),
            render: () => <InsightOptionFilter
                label={p.supplierDataName}
                selection={editing.s_ids ?? DEFAULT}
                exclude={['s_ids', 's_ids__not']}
                source={sources.supplier}
                showOptionSubLabel={p.p.showSupplierIdInAdvDashboard}
                onChange={s => ({s_ids: s as SelectionResult<string>})}/>,
        },
        !hideParents && {
            id: 'sp_ids', label: parentLabel, primary: false,
            isActive: f => MultiSelection.isActive(f.sp_ids),
            render: () => <InsightOptionFilter
                label={parentLabel}
                selection={editing.sp_ids ?? DEFAULT}
                exclude={['sp_ids', 'sp_ids__not']}
                source={sources.parentSupplier}
                onChange={s => ({sp_ids: s as SelectionResult<string>})}/>,
        },
        {
            id: 'date_range', label: 'Date', primary: true,
            isActive: f => Boolean(f.date_range),
            render: () => <DateRangeFilter/>,
        },
        // Available everywhere; only the business unit table is limited to synergyDashboardShowBusinessUnits
        {
            id: 'business_unit_ids', label: p.buDataName, primary: true,
            isActive: f => MultiSelection.isActive(f.business_unit_ids),
            render: () => <InsightOptionFilter
                label={p.buDataName}
                selection={editing.business_unit_ids ?? DEFAULT}
                exclude={['business_unit_ids', 'business_unit_ids__not']}
                source={sources.businessUnit}
                showOptionSubLabel={p.p.showBuWithId}
                onChange={s => ({business_unit_ids: s as SelectionResult<number>})}/>,
        },
        ...manager.buGroupFilterColumns.map(col => {
            const field = col.column_name as BuGroupField;
            const label = col.display_name || col.column_name;
            return {
                id: field, label, primary: false,
                isActive: (f: AdvancedFilter) => MultiSelection.isActive(f[field]),
                render: () => <InsightOptionFilter
                    label={label}
                    selection={editing[field] ?? DEFAULT}
                    exclude={[field, `${field}__not`]}
                    source={sources.buGroup(field)}
                    onChange={s => ({[field]: s as SelectionResult<string>})}/>,
            };
        }),
        Boolean(p.p.synergyDashboardShowCDescriptionFilter) && {
            id: 'c_descriptions', label: p.cDescriptionDataName, primary: false,
            isActive: f => (f.c_descriptions?.length ?? 0) > 0,
            render: () => <InsightOptionFilter
                label={p.cDescriptionDataName}
                selection={listSelection(editing.c_descriptions)}
                exclude={['c_descriptions']}
                source={sources.cDescription}
                inclusiveOnly
                onChange={s => ({c_descriptions: listFromSelection(s)})}/>,
        },
        Boolean(p.p.synergyDashboardShowPlantFilter) && {
            id: 'plants', label: p.plantDataName, primary: false,
            isActive: f => (f.plants?.length ?? 0) > 0,
            render: () => <InsightOptionFilter
                label={p.plantDataName}
                selection={listSelection(editing.plants)}
                exclude={['plants']}
                source={sources.plant}
                inclusiveOnly
                onChange={s => ({plants: listFromSelection(s)})}/>,
        },
        Boolean(p.p.synergyDashboardShowCAddressFilter) && {
            id: 'c_address', label: 'Region', primary: false,
            isActive: f => (f.c_address?.length ?? 0) > 0,
            render: () => <InsightOptionFilter
                label="Region"
                selection={listSelection(editing.c_address)}
                exclude={['c_address']}
                source={sources.cAddress}
                inclusiveOnly
                onChange={s => ({c_address: listFromSelection(s)})}/>,
        },
    ];
    const present = definitions.filter((d): d is FilterDefinition => Boolean(d));
    // Always-shown filters first, in this order; the rest keep their order in "Add filter"
    const rank = (d: FilterDefinition) => {
        const i = PRIMARY_ORDER.indexOf(d.id);
        return i === -1 ? PRIMARY_ORDER.length : i;
    };
    return [...present].sort((a, b) => rank(a) - rank(b));
}
